/**
 * Web Vitals Performance Tracking Service
 * Epic 18: Marketing Site Performance Optimization
 * Tracks Core Web Vitals: LCP, FID, CLS, FCP, TTFB
 */

import { getCLS, getFCP, getFID, getLCP, getTTFB, Metric } from 'web-vitals';

interface PerformanceMetrics {
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  fcp?: number; // First Contentful Paint
  ttfb?: number; // Time to First Byte
  timestamp: string;
  page: string;
  visitorId?: string;
}

// Performance thresholds for monitoring
const PERFORMANCE_TARGETS = {
  lcp: 2500, // LCP < 2.5s (Good)
  fid: 100, // FID < 100ms (Good)
  cls: 0.1, // CLS < 0.1 (Good)
  fcp: 1800, // FCP < 1.8s (Good)
  ttfb: 600, // TTFB < 600ms (Good)
};

class WebVitalsService {
  private metrics: Partial<PerformanceMetrics> = {};
  private apiEndpoint: string = '/api/v1/marketing/analytics/vitals';
  private batchedMetrics: PerformanceMetrics[] = [];
  private batchTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.initializeTracking();
  }

  /**
   * Initialize Web Vitals tracking
   */
  private initializeTracking(): void {
    // Track Core Web Vitals
    getCLS(this.handleCLS.bind(this));
    getFCP(this.handleFCP.bind(this));
    getFID(this.handleFID.bind(this));
    getLCP(this.handleLCP.bind(this));
    getTTFB(this.handleTTFB.bind(this));

    // Track page visibility changes to send metrics
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.sendMetrics();
      }
    });

    // Send metrics on page unload
    window.addEventListener('beforeunload', () => {
      this.sendMetrics();
    });
  }

  /**
   * Handle Cumulative Layout Shift (CLS)
   */
  private handleCLS(metric: Metric): void {
    this.metrics.cls = metric.value;
    this.checkPerformanceTarget('cls', metric.value);
    this.queueMetrics();
  }

  /**
   * Handle First Contentful Paint (FCP)
   */
  private handleFCP(metric: Metric): void {
    this.metrics.fcp = metric.value;
    this.checkPerformanceTarget('fcp', metric.value);
    this.queueMetrics();
  }

  /**
   * Handle First Input Delay (FID)
   */
  private handleFID(metric: Metric): void {
    this.metrics.fid = metric.value;
    this.checkPerformanceTarget('fid', metric.value);
    this.queueMetrics();
  }

  /**
   * Handle Largest Contentful Paint (LCP)
   */
  private handleLCP(metric: Metric): void {
    this.metrics.lcp = metric.value;
    this.checkPerformanceTarget('lcp', metric.value);

    // LCP is typically the last metric, check if we meet all targets
    if (this.metrics.lcp < PERFORMANCE_TARGETS.lcp) {
    }
    this.queueMetrics();
  }

  /**
   * Handle Time to First Byte (TTFB)
   */
  private handleTTFB(metric: Metric): void {
    this.metrics.ttfb = metric.value;
    this.checkPerformanceTarget('ttfb', metric.value);
    this.queueMetrics();
  }

  /**
   * Check if metric meets performance target
   */
  private checkPerformanceTarget(metricName: keyof typeof PERFORMANCE_TARGETS, value: number): void {
    const target = PERFORMANCE_TARGETS[metricName];
    const status = value <= target ? '✅' : '⚠️';

    if (import.meta.env.DEV) {
    }

    // Log warning if performance target is missed
    if (value > target * 1.5) {
      console.warn(`Performance degradation detected: ${metricName} is ${value.toFixed(2)}ms (target: ${target}ms)`);
    }
  }

  /**
   * Queue metrics for batched sending
   */
  private queueMetrics(): void {
    // Clear existing timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    // Set new timer to batch metrics
    this.batchTimer = setTimeout(() => {
      this.sendMetrics();
    }, 5000); // Send after 5 seconds of inactivity
  }

  /**
   * Send metrics to analytics endpoint
   */
  private async sendMetrics(): Promise<void> {
    // Only send if we have meaningful metrics
    if (!this.metrics.lcp && !this.metrics.fcp && !this.metrics.cls) {
      return;
    }

    const metricsData: PerformanceMetrics = {
      ...this.metrics,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      visitorId: this.getVisitorId(),
    };

    // Add to batch
    this.batchedMetrics.push(metricsData);

    // Send metrics using beacon API for reliability
    if (navigator.sendBeacon) {
      const data = JSON.stringify({
        metrics: this.batchedMetrics,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });

      navigator.sendBeacon(this.apiEndpoint, data);
      this.batchedMetrics = []; // Clear batch after sending
    } else {
      // Fallback to fetch API with timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            metrics: this.batchedMetrics,
            userAgent: navigator.userAgent,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
          }),
          keepalive: true, // Keep request alive even if page unloads
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        this.batchedMetrics = [];
      } catch (error) {
        // Silently fail - analytics should not break user experience
        if (import.meta.env.DEV) {
          console.warn('Failed to send Web Vitals metrics (endpoint may not be available):', error);
        }
        this.batchedMetrics = []; // Clear batch even on failure
      }
    }
  }

  /**
   * Get or generate visitor ID
   */
  private getVisitorId(): string {
    let visitorId = localStorage.getItem('sigma08_visitor_id');
    if (!visitorId) {
      visitorId = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('sigma08_visitor_id', visitorId);
    }
    return visitorId;
  }

  /**
   * Manually report custom performance marks
   */
  public reportCustomMetric(name: string, value: number): void {
    // Send custom metric
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        this.apiEndpoint,
        JSON.stringify({
          custom: {
            name,
            value,
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            visitorId: this.getVisitorId(),
          },
        })
      );
    }
  }

  /**
   * Get current performance score
   */
  public getPerformanceScore(): number {
    let score = 100;
    let metricsCount = 0;

    // Calculate score based on how well we meet targets
    if (this.metrics.lcp !== undefined) {
      const lcpScore = Math.max(0, 100 - ((this.metrics.lcp - PERFORMANCE_TARGETS.lcp) / PERFORMANCE_TARGETS.lcp) * 50);
      score = (score + lcpScore) / 2;
      metricsCount++;
    }

    if (this.metrics.fid !== undefined) {
      const fidScore = Math.max(0, 100 - ((this.metrics.fid - PERFORMANCE_TARGETS.fid) / PERFORMANCE_TARGETS.fid) * 50);
      score = (score + fidScore) / 2;
      metricsCount++;
    }

    if (this.metrics.cls !== undefined) {
      const clsScore = Math.max(0, 100 - ((this.metrics.cls - PERFORMANCE_TARGETS.cls) / PERFORMANCE_TARGETS.cls) * 50);
      score = (score + clsScore) / 2;
      metricsCount++;
    }

    return metricsCount > 0 ? Math.round(score) : 0;
  }

  /**
   * Get current metrics snapshot
   */
  public getMetrics(): Partial<PerformanceMetrics> {
    return { ...this.metrics };
  }
}

// Export singleton instance
export const webVitalsService = new WebVitalsService();

// Export utility to initialize tracking (called in app entry point)
export const initWebVitals = (): void => {
  if (typeof window !== 'undefined') {
    // Service auto-initializes
  }
};

// Export performance targets for reference
export { PERFORMANCE_TARGETS };
